// @ts-nocheck
/**
 * The Live Sheet's secondary tabs.
 *
 * These are different tables, not filtered views of the Teams grid: each tab
 * owns its columns, its edit rules and its save path. The editable set here
 * MUST stay a subset of the sheet-tab edge function's allowlist — the server
 * decides, this is only what the UI offers.
 */

export type TabKey =
  | "teams"
  | "roster"
  | "checkin"
  | "coaches"
  | "media"
  | "extras"
  | "tickets";

export interface TabColumn {
  id: string;
  title: string;
  width?: number;
  kind?: "text" | "number" | "money" | "date" | "bool" | "select" | "link" | "game";
  editable?: boolean;
  /** Option list key resolved at render time (venues, divisions…). */
  optionsKey?: string;
}

export interface TabDef {
  key: TabKey;
  label: string;
  /** Scope written onto a share link created from this tab. */
  scope: "event" | "roster" | "checkin" | "payment";
  /** Table the counts come from; null when the count isn't meaningful. */
  countTable: string | null;
  columns: TabColumn[];
  readOnly?: boolean;
  /** Age-group filter chips across the top (the printed check-in sheet). */
  ageFilter?: boolean;
  printable?: boolean;
  /** Shown instead of a grid when the tab has no rows at all. */
  emptyMessage?: string;
}

export const TAB_DEFS: TabDef[] = [
  { key: "teams", label: "Teams", scope: "event", countTable: "event_registrations_v2", columns: [] },

  {
    key: "roster",
    label: "Rosters",
    scope: "roster",
    countTable: "event_registrations_v2",
    readOnly: true,
    columns: [
      { id: "team_name", title: "Team", width: 220 },
      { id: "division", title: "Division", width: 110 },
      { id: "head_coach", title: "Head Coach", width: 170 },
      { id: "coach_email", title: "Coach Email", width: 220 },
      { id: "coach_phone", title: "Coach Phone", width: 140 },
      { id: "players", title: "Players", width: 90 },
      { id: "base_roster", title: "Base Roster", width: 110 },
      { id: "roster_state", title: "Roster Status", width: 170 },
      { id: "submitted_at", title: "Submitted", width: 130, kind: "date" },
      { id: "roster_url", title: "Edit on RecruitLook", width: 160, kind: "link" },
    ],
  },

  {
    key: "checkin",
    label: "Check-in",
    scope: "checkin",
    countTable: "event_registrations_v2",
    ageFilter: true,
    printable: true,
    columns: [
      { id: "division", title: "Division", width: 100 },
      { id: "team_name", title: "Team", width: 210 },
      {
        id: "checkin_location",
        title: "Check-in Location",
        width: 180,
        kind: "select",
        optionsKey: "venues",
        editable: true,
      },
      { id: "arrived", title: "Checked in", width: 100, kind: "bool", editable: true },
      { id: "hc_signature", title: "HC Signature", width: 110, kind: "bool", editable: true },
      { id: "hc_name_override", title: "Head Coach", width: 170, editable: true },
      { id: "hc_phone_override", title: "HC Phone", width: 140, editable: true },
      { id: "ac_name_override", title: "Assistant Coach", width: 170, editable: true },
      { id: "ac_present", title: "AC Checked in", width: 110, kind: "bool", editable: true },
      { id: "ac_signature", title: "AC Signature", width: 110, kind: "bool", editable: true },
      { id: "ac_phone_override", title: "AC Phone", width: 140, editable: true },
      { id: "staff_notes", title: "Notes", width: 220, editable: true },
    ],
  },

  {
    key: "coaches",
    label: "Coaches",
    scope: "checkin",
    countTable: "event_coaches",
    emptyMessage: "No college coaches registered for this event yet.",
    columns: [
      { id: "filler_name", title: "Filler Name", width: 160, editable: true },
      { id: "filler_email", title: "Filler Email", width: 210, editable: true },
      { id: "coach_name", title: "Coach", width: 170, editable: true },
      { id: "coach_email", title: "Coach Email", width: 210, editable: true },
      { id: "coach_phone", title: "Phone", width: 140, editable: true },
      { id: "college_name", title: "College", width: 190, editable: true },
      { id: "division", title: "Division", width: 110, editable: true },
      { id: "additional_coaches", title: "Additional Coaches", width: 200, editable: true },
      {
        id: "additional_coach_count",
        title: "Additional Coach Count",
        width: 150,
        kind: "number",
        editable: true,
      },
      {
        id: "hospitality_days",
        title: "Hospitality Days",
        width: 130,
        kind: "number",
        editable: true,
      },
      // Stripe owns these two.
      { id: "payment_status", title: "Payment Status", width: 140 },
      { id: "amount_paid_cents", title: "Amount Paid", width: 120, kind: "money" },
      { id: "is_ncaa_live", title: "NCAA Live", width: 100, kind: "bool", editable: true },
      {
        id: "hide_from_public",
        title: "Hide From Public",
        width: 120,
        kind: "bool",
        editable: true,
      },
      { id: "notes", title: "Notes", width: 240, editable: true },
    ],
  },

  {
    key: "media",
    label: "Media",
    scope: "event",
    countTable: "media_credentials",
    emptyMessage: "No media credential requests for this event yet.",
    columns: [
      { id: "name", title: "Name", width: 170, editable: true },
      { id: "email", title: "Email", width: 210, editable: true },
      { id: "phone", title: "Phone", width: 140, editable: true },
      { id: "organization", title: "Organization", width: 190, editable: true },
      { id: "instagram_handle", title: "Instagram", width: 140, editable: true },
      { id: "twitter_handle", title: "Twitter", width: 140, editable: true },
      { id: "coverage_type", title: "Coverage Type", width: 150, editable: true },
      { id: "created_at", title: "Submitted", width: 130, kind: "date" },
      { id: "payment_status", title: "Payment Status", width: 140 },
      { id: "amount_paid_cents", title: "Amount", width: 110, kind: "money" },
      { id: "processing_fee_cents", title: "Card Fee", width: 100, kind: "money" },
      { id: "status", title: "Status", width: 120 },
      { id: "denial_reason", title: "Denial Reason", width: 220 },
    ],
  },

  {
    key: "extras",
    label: "Extras",
    scope: "payment",
    countTable: "purchased_extras",
    columns: [
      { id: "extra_name", title: "Extra", width: 200 },
      { id: "extra_price", title: "Price", width: 100, kind: "money" },
      { id: "team_name", title: "Team", width: 190 },
      { id: "division", title: "Division", width: 110 },
      { id: "player_name", title: "Player", width: 170, editable: true },
      { id: "player_jersey_number", title: "Jersey", width: 90, editable: true },
      { id: "player_instagram_handle", title: "Instagram", width: 140, editable: true },
      { id: "player_x_handle", title: "X", width: 130, editable: true },
      { id: "buyer_email", title: "Buyer Email", width: 220 },
      { id: "payment_status", title: "Payment Status", width: 140 },
      {
        id: "fulfilment_status",
        title: "Fulfilment",
        width: 150,
        kind: "select",
        optionsKey: "fulfilment_statuses",
        editable: true,
      },
      { id: "fulfilment_notes", title: "Fulfilment Notes", width: 220, editable: true },
      // A game, not a day: a team plays several a day and a shoot needs the game.
      { id: "scheduled_game_id", title: "Game", width: 300, kind: "game", editable: true },
      { id: "qr_code", title: "QR Code", width: 150 },
      { id: "is_redeemed", title: "Redeemed", width: 100, kind: "bool" },
    ],
  },


  {
    key: "tickets",
    label: "Tickets",
    scope: "payment",
    countTable: "event_ticket_purchases",
    columns: [
      { id: "purchaser_name", title: "Name", width: 180, editable: true },
      { id: "purchaser_email", title: "Email", width: 220, editable: true },
      { id: "ticket_name", title: "Ticket Type", width: 180, editable: true },
      { id: "quantity", title: "Qty", width: 70, kind: "number" },
      { id: "price", title: "Price", width: 110, kind: "money" },
      { id: "payment_status", title: "Payment Status", width: 140 },
      { id: "checked_in", title: "Checked in", width: 110, kind: "bool", editable: true },
      { id: "notes", title: "Notes", width: 220, editable: true },
    ],
  },
];

export const TAB_BY_KEY: Record<TabKey, TabDef> = Object.fromEntries(
  TAB_DEFS.map((t) => [t.key, t])
) as Record<TabKey, TabDef>;

/** A share link carries its tab's scope, so it reopens where it was made. */
export const tabForScope = (scope: string | null | undefined): TabKey => {
  const match = TAB_DEFS.find((t) => t.scope === scope && t.key !== "teams");
  return scope === "event" || !match ? "teams" : match.key;
};
